import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Box,
} from "@mui/material";
import { useAppSelector } from "redux/hooks";
import { selectCase, selectCaseCollaboratorFirms, setCollaboratorFirms } from "redux/slices/case";
import { useMemo, useState } from "react";
import { RemoveCircle } from "@mui/icons-material";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { P } from "theme/typography";
import PSelect from "components/PSelect";
import theme from "theme";
import { useFirmsByLensId } from "helpers/hooks/useAllowedLenses";

export interface CollaboratorFirmPickerDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  lensId: number;
}

const CollaboratorFirmPickerDialog = (props: CollaboratorFirmPickerDialogProps) => {
  const { open, setOpen, lensId } = props;
  const lensCase = useAppSelector(selectCase);
  const allFirms = useFirmsByLensId(lensId);
  const firms = allFirms.filter((f) => f.id !== lensCase.plaintiffFirm?.id);
  const firmsMap = new Map(firms.map((f) => [f.id, f]));
  const collaboratorFirms = useAppSelector(selectCaseCollaboratorFirms);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCollaboratorFirms, setSelectedCollaboratorFirms] = useState(collaboratorFirms);
  const selectedCollaboratorFirmIds = useMemo(
    () => selectedCollaboratorFirms.map((f) => f.id),
    [selectedCollaboratorFirms]
  );

  const snackpatch = useAppDispatchWithSnackbar();

  const handleCancel = () => {
    setOpen(false);
  };

  const handleSave = () => {
    setIsSaving(true);
    snackpatch(setCollaboratorFirms({ caseId: lensCase.id, firms: selectedCollaboratorFirms }), {
      successMessage: "Collaborator firms updated successfully",
      errorMessage: "Unable to update collaborator firms",
    })
      .then(() => {
        setIsSaving(false);
        setOpen(false);
      })
      .catch(() => {
        setIsSaving(false);
      });
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogContent>
        <Box sx={{ pb: 3 }}>
          <P variant="xlBold">Collect Sharing Permissions</P>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", pb: 2 }}>
          <P variant="baseBold">Primary Firm</P>
          <P variant="sm">{lensCase.plaintiffFirm?.name}</P>
        </Box>
        <Box>
          <P variant="baseBold">Collaborator Firms</P>
        </Box>
        <FormControl size="small" fullWidth sx={{ mt: 1 }}>
          <InputLabel id="multiple-firm-select">Add Collaborator Firm</InputLabel>
          <PSelect
            multiple
            labelId="firm-select"
            label="Add Collaborator Firm"
            value={selectedCollaboratorFirmIds}
            size="small"
            onChange={(e) => {
              const ids = e.target.value as number[];
              setSelectedCollaboratorFirms(
                ids.flatMap((firmId) => {
                  const firm = firmsMap.get(firmId);
                  if (firm !== undefined) {
                    return [{ id: firm.id, name: firm.name, externalId: firm.externalId }];
                  }
                  return [];
                })
              );
            }}
            fullWidth
            MenuProps={{ sx: { maxHeight: "400px" } }}
            sx={{ minWidth: "200px" }}>
            {firms.map((firm) => (
              <MenuItem key={firm.id} value={firm.id}>
                {firm.name}
              </MenuItem>
            ))}
          </PSelect>
        </FormControl>
        {selectedCollaboratorFirms.length > 0 && (
          <Box
            sx={{
              backgroundColor: theme.palette.grey[50],
              borderRadius: 1,
              p: 1,
              maxHeight: "200px",
              overflow: "auto",
              mt: 1,
            }}>
            {selectedCollaboratorFirms.map((collaboratorFirm) => {
              return (
                <Box key={collaboratorFirm.id} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setSelectedCollaboratorFirms(
                        selectedCollaboratorFirms.filter((value) => value.id !== collaboratorFirm.id)
                      )
                    }>
                    <RemoveCircle />
                  </IconButton>
                  <P>{collaboratorFirm.name}</P>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="text" disabled={isSaving} onClick={handleCancel}>
          Cancel
        </Button>
        <Button color="primary" variant="contained" disabled={isSaving} onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollaboratorFirmPickerDialog;
